<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <!-- Only show in DEV mode -->
  <div v-if="isDev">
    <!-- Seed Database Button -->
    <button
      @click="handleSeed"
      :disabled="loading"
      class="w-full flex items-center gap-2 bg-purple-100 dark:bg-purple-900 p-4 rounded-lg shadow hover:bg-purple-200 dark:hover:bg-purple-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-2 border-purple-400 dark:border-purple-600">
      <div class="flex-1 text-left">
        <div class="font-semibold text-purple-900 dark:text-purple-100">
          {{ loading ? 'Seeding Database...' : 'Seed Database (Dev)' }}
        </div>
        <div class="text-xs text-purple-700 dark:text-purple-300">
          {{ statusMessage || 'Clear + seed with 5 groups & 20 goats' }}
        </div>
      </div>
    </button>

    <!-- Success/Error Toast -->
    <Toast :show="showToast" :message="toastMessage" :type="toastType" />
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue'
  import { seedAll } from '@/utils/seeding/seed.js'
  import Toast from '@/components/ui/feedback/Toast.vue'

  // Check if we're in development mode
  // Uses VITE_ENV from .env file so dev tools work even in production builds
  const isDev = computed(() => import.meta.env.VITE_ENV === 'development')

  // Component state
  const loading = ref(false)
  const statusMessage = ref('')
  const showToast = ref(false)
  const toastMessage = ref('')
  const toastType = ref('saved')
  /**
   * Handle seed button click
   */
  async function handleSeed() {
    loading.value = true
    statusMessage.value = 'Clearing database...'

    try {
      // Seed with default options (20 goats)
      await seedAll({ goatCount: 20 })

      // Success!
      statusMessage.value = 'Seeded successfully!'
      showSuccessToast('Database seeded with 5 groups & 20 goats!')

      // Reset status message after delay
      setTimeout(() => {
        statusMessage.value = ''
      }, 3000)
    } catch (error) {
      console.error('Failed to seed database:', error)
      statusMessage.value = 'Seeding failed'
      showErrorToast(`Seeding failed: ${error.message}`)

      // Reset status message after delay
      setTimeout(() => {
        statusMessage.value = ''
      }, 3000)
    } finally {
      loading.value = false
    }
  }

  function showSuccessToast(message) {
    toastType.value = 'info'
    toastMessage.value = message
    showToast.value = true
  }

  function showErrorToast(message) {
    toastType.value = 'error'
    toastMessage.value = message
    showToast.value = true
  }
</script>
